import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { saveAs } from "file-saver";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import AlertBanner from "../../../DesignSystem/AlertBanner/AlertBanner";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import {
  createAdviserGatingWorkbook,
  explodeAdviserGatingRows,
  fetchAdviserGatingRows,
  applyTemplatePermissions,
  readAdviserGatingTemplate,
} from "./adviserGatingService";
import "../../../../css/Adviser/EditAdviserDetailsPage.css";
import "../../../../css/Adviser/IOBulkUpload.css";

export default function AdviserGatingPage() {
  const { userPermissions } = useUserPermissions();
  const canView = userPermissions.some((permission) =>
    ["IO Admin", "Administrator"].includes(permission),
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [file, setFile] = useState(null);
  const [template, setTemplate] = useState(null);
  const [analysing, setAnalysing] = useState(false);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    fetchAdviserGatingRows()
      .then((value) => {
        if (active) setRows(value);
      })
      .catch((loadError) => {
        console.error("Failed to load adviser gating data:", loadError);
        if (active) setError(loadError.message || "Could not load adviser gating data.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [canView]);

  const populatedRows = useMemo(
    () => (template ? applyTemplatePermissions(rows, template) : rows),
    [rows, template],
  );
  const excelRows = useMemo(
    () => (template ? explodeAdviserGatingRows(populatedRows) : []),
    [populatedRows, template],
  );

  const visibleRows = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return excelRows;
    return excelRows.filter((row) =>
      [
        row.adviserCRMContactID,
        row.adviserName,
        row.email,
        row.status.join(" "),
        row.authorisedProductType,
      ]
        .join(" ")
        .toLowerCase()
        .includes(search),
    );
  }, [query, excelRows]);

  const handleExport = async () => {
    try {
      setExporting(true);
      setError("");
      const buffer = await createAdviserGatingWorkbook(excelRows, template);
      const date = new Date().toISOString().slice(0, 10);
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `UpdateAdviserGating-${date}.xlsx`,
      );
    } catch (exportError) {
      console.error("Failed to export adviser gating workbook:", exportError);
      setError(exportError.message || "Could not create the Excel workbook.");
    } finally {
      setExporting(false);
    }
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setTemplate(null);
    setError("");
    if (!selectedFile) return;

    try {
      setAnalysing(true);
      setTemplate(await readAdviserGatingTemplate(selectedFile));
    } catch (templateError) {
      console.error("Failed to read adviser gating template:", templateError);
      setError(templateError.message || "Could not read the uploaded workbook.");
    } finally {
      setAnalysing(false);
    }
  };

  return (
    <div className="main-content">
      <Helmet><title>Adviser Gating</title></Helmet>
      <Sidebar />
      <div className="Main-Container">
        <div className="smFeed">
          <Breadcrumb items={[{ label: "Intelliflo", to: "/Department/Intelliflo" }, { label: "Adviser Gating" }]} />
          {!canView ? (
            <AlertBanner type="error" message="You need IO Admin or Administrator permission to view adviser gating." showClose={false} />
          ) : (
            <>
              <div className="smEditActions">
                <div>
                  <h3 className="FP_H3">Adviser Gating</h3>
                  <p className="Content-Small muted">
                    All advisers, sorted by name. The uploaded permission columns are combined; advisers with both FP and MA receive Financial Planners, Mortgage and Protection.
                  </p>
                </div>
                <PrimaryButton label={exporting ? "Creating Excel..." : "Download Excel"} onClick={handleExport} disabled={loading || analysing || exporting || excelRows.length === 0 || !template} />
              </div>
              {error ? <AlertBanner type="error" message={error} onClose={() => setError("")} /> : null}
              <div className="ioGatingUpload">
                <label className="FP_Content_Semibold" htmlFor="adviser-gating-template">Upload gating workbook</label>
                <input id="adviser-gating-template" type="file" accept=".xlsx" onChange={handleFileChange} />
                <span className="Content-Small muted">
                  {analysing ? "Reading workbook..." : template ? `${file.name} is ready to populate.` : "Upload UpdateAdviserGating.xlsx to enable the download."}
                </span>
              </div>
              <div className="ioGatingToolbar">
                <input className="ioGatingSearch Content-Small" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search adviser, ID, status or product type" />
                <span className="Content-Small muted">{visibleRows.length} of {excelRows.length} permission rows ({populatedRows.length} advisers)</span>
              </div>
              <div className="ioImportTableWrap ioGatingTableWrap">
                <table className="ioImportTable">
                  <thead><tr><th>Adviser CRM Contact ID</th><th>First Name</th><th>Last Name</th><th>Email / Document ID</th><th>Status</th><th>Authorised Product Types</th></tr></thead>
                  <tbody>
                    {loading ? <tr><td colSpan="6">Loading adviser data...</td></tr> : null}
                    {!loading && visibleRows.length === 0 ? <tr><td colSpan="6">{template ? "No adviser permission rows found." : "Upload the gating workbook to populate the preview."}</td></tr> : null}
                    {!loading && visibleRows.map((row) => (
                      <tr key={`${row.adviserKey}-${row.productIndex}`}>
                        <td>{row.adviserCRMContactID || "-"}</td>
                        <td>{row.firstName || "-"}</td>
                        <td>{row.lastName || "-"}</td>
                        <td>{row.email}</td>
                        <td>{row.status.join(", ") || "-"}</td>
                        <td>{row.authorisedProductType}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
